/**
 * @copyright (C) 2023-2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-21 16:36:46
 **/

import { BaseServiceV2 } from "@/frameworks/libraries/restservice";

class Account extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/account${path ? `/${path}` : ""}`;

	public generalRoles = {
		name: "generalRoles",
		execute: (
			parameter: WebServices.Account.GeneralRoles.Request = {}
		): Promise<WebServices.Account.GeneralRoles.Response> =>
			this.get(this.generalRoles.name, this.APIPath("general_roles"), parameter)
	};

	public signUp = {
		name: "signUp",
		execute: (parameter: WebServices.Account.SignUp.Request): Promise<WebServices.Account.SignUp.Response> =>
			this.post(this.signUp.name, this.APIPath("signup"), parameter)
	};

	public signupByEmailInvite = {
		name: "email_invite",
		execute: (
			parameter: WebServices.Account.SignupByEmailInvite.Request
		): Promise<WebServices.Account.SignupByEmailInvite.Response> =>
			this.post(this.signupByEmailInvite.name, this.APIPath("signup/email_invite"), parameter)
	};

	public socialSignUp = {
		name: "socialSignUp",
		execute: ({
			idp,
			...parameter
		}: WebServices.Account.SocialSignUp.Request): Promise<WebServices.Account.SocialSignUp.Response> =>
			this.post(this.socialSignUp.name, this.APIPath(`${idp}/signup`), parameter)
	};

	public login = {
		name: "login",
		execute: (parameter: WebServices.Login.Request): Promise<WebServices.Login.Response> =>
			this.post(this.login.name, "/api/login", parameter)
	};

	public logout = {
		name: "logout",
		execute: (parameter: WebServices.Logout.Request): Promise<WebServices.Logout.Response> =>
			this.post(this.logout.name, "/api/logout", parameter)
	};

	public logoutOthers = {
		name: "logoutOthers",
		execute: (parameter: WebServices.LogoutOthers.Request): Promise<WebServices.LogoutOthers.Response> =>
			this.post(this.logoutOthers.name, "/api/logout/other_devices", parameter)
	};

	public partnerLogin = {
		name: "partnerLogin",
		execute: (
			parameter: WebServices.PartnerLogin.Login.Request
		): Promise<WebServices.PartnerLogin.Login.Response> =>
			this.post(this.partnerLogin.name, "/api/partner_admin/partners/login", parameter)
	};

	public loginLog = {
		name: "loginLog",
		execute: (parameter: WebServices.Log.Request = {}): Promise<WebServices.Log.Response> =>
			this.get(this.loginLog.name, "/api/login_logs", parameter)
	};

	public joinedStatus = {
		name: "joinedStatus",
		execute: (
			parameter: WebServices.Account.GeneralRoles.Request = {}
		): Promise<WebServices.Account.GeneralRoles.Response> =>
			this.get(this.joinedStatus.name, this.APIPath("joined_status"), parameter)
	};
}

class Password extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/account/password${path ? `/${path}` : ""}`;

	public reset = {
		name: "password:reset",
		execute: (
			parameter: WebServices.Account.Password.Reset.Request
		): Promise<WebServices.Account.Password.Reset.Response> =>
			this.post(this.reset.name, this.APIPath("reset"), parameter)
	};

	public extendUpdateDate = {
		name: "password:extendUpdateDate",
		execute: (
			parameter: WebServices.Account.Password.ExtendUpdateDate.Request = {}
		): Promise<WebServices.Account.Password.ExtendUpdateDate.Response> =>
			this.post(this.extendUpdateDate.name, this.APIPath("extend_update_date"), parameter)
	};
}

class ResetPassword extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/account/reset_password_email${path ? `/${path}` : ""}`;

	public inquiry = {
		name: "resetPassword:inquiry",
		execute: ({
			id
		}: WebServices.Account.ResetPassword.Inquiry.Request = {}): Promise<WebServices.Account.ResetPassword.Inquiry.Response> =>
			this.get(this.inquiry.name, this.APIPath(id))
	};

	public send = {
		name: "resetPassword:send",
		execute: (
			parameter: WebServices.Account.ResetPassword.Send.Request
		): Promise<WebServices.Account.ResetPassword.Send.Response> =>
			this.post(this.send.name, this.APIPath("send"), parameter)
	};

	public reset = {
		name: "resetPassword:reset",
		execute: (
			parameter: WebServices.Account.ResetPassword.Reset.Request
		): Promise<WebServices.Account.ResetPassword.Reset.Response> =>
			this.post(this.reset.name, this.APIPath("reset"), parameter)
	};
}

class Authentication extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/account/authentication_code${path ? `/${path}` : ""}`;

	public send = {
		name: "authentication:send",
		execute: (
			parameter: WebServices.Account.Authentication.Send.Request
		): Promise<WebServices.Account.Authentication.Send.Response> =>
			this.post(this.send.name, this.APIPath("send"), parameter)
	};

	public resend = {
		name: "authentication:resend",
		execute: (
			parameter: WebServices.Account.Authentication.Resend.Request = {}
		): Promise<WebServices.Account.Authentication.Resend.Response> =>
			this.post(this.resend.name, this.APIPath("resend"), parameter)
	};

	public authenticate = {
		name: "authentication:authenticate",
		execute: (
			parameter: WebServices.Account.Authentication.Authenticate.Request
		): Promise<WebServices.Account.Authentication.Authenticate.Response> =>
			this.post(this.authenticate.name, this.APIPath("authenticate"), parameter)
	};

	public refreshToken = {
		name: "authentication:refreshToken",
		execute: (
			parameter: WebServices.Account.Authentication.ReissueToken.Request
		): Promise<WebServices.Account.Authentication.ReissueToken.Response> =>
			this.post(this.refreshToken.name, "/api/reissue_token", parameter)
	};
}

export default class AccountService {
	constructor(
		events: API.Events,
		public readonly account = new Account(),
		public readonly password = new Password(),
		public readonly resetPassword = new ResetPassword(),
		public readonly authentication = new Authentication()
	) {
		account.set_event(events);
		password.set_event(events);
		resetPassword.set_event(events);
		authentication.set_event(events);
	}
}
